/**
 * cm-shorts — behaviour for the 3D Anti-Chafe Shorts product page.
 *
 * Three responsibilities:
 *   1. the quantity-break bundle widget (deal selection + one variant picker
 *      per unit in the deal), mirroring how the Kaching Bundles app behaves on
 *      the reference page;
 *   2. add-to-cart, which posts every unit of the selected deal in one call,
 *      applies the deal's discount code if one is configured, and then sends
 *      the customer straight to checkout;
 *   3. the gallery lightbox;
 *   4. the sticky offset of the buy box, so Add to cart stays on screen while
 *      the desktop gallery scrolls.
 */

const cartAddUrl = window.Shopify?.routes?.root
  ? `${window.Shopify.routes.root}cart/add.js`
  : '/cart/add.js';
const checkoutUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}checkout` : '/checkout';

/* -------------------------------------------------------------------------- */
/* Bundle                                                                     */
/* -------------------------------------------------------------------------- */

class CmShortsBundle extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-cm-sh-variants]');
    this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    this.optionNames = JSON.parse(this.dataset.optionNames || '[]');
    this.deals = Array.from(this.querySelectorAll('.cm-sh-deal'));
    this.button = this.querySelector('.cm-sh-atc');
    this.error = this.querySelector('.cm-sh-error');

    this.deals.forEach((deal) => {
      deal.querySelector('.cm-sh-deal__container')?.addEventListener('click', (event) => {
        // Let the pickers inside the open deal work without re-selecting it.
        if (event.target.closest('.cm-sh-unit')) return;
        this.select(deal);
      });

      deal.querySelectorAll('.cm-sh-unit').forEach((unit) => this.bindUnit(unit));
    });

    this.button?.addEventListener('click', () => this.addToCart());

    document.addEventListener('click', (event) => {
      if (event.target.closest('.cm-sh-swatch-dropdown')) return;
      this.closeDropdowns();
    });

    const preselected = this.deals.find((deal) => deal.dataset.selected === 'true');
    this.select(preselected || this.deals[0]);
  }

  /* ---- selection ---- */

  select(deal) {
    if (!deal) return;
    this.deals.forEach((item) => {
      const active = item === deal;
      item.classList.toggle('is-selected', active);
      const input = item.querySelector('.cm-sh-deal__input');
      if (input) input.checked = active;
    });
    this.selectedDeal = deal;
    this.clearError();
  }

  /* ---- one unit of the bundle ---- */

  bindUnit(unit) {
    unit.querySelectorAll('select.cm-sh-select').forEach((select) => {
      select.addEventListener('change', () => this.syncUnit(unit));
    });

    const dropdown = unit.querySelector('.cm-sh-swatch-dropdown');
    if (dropdown) {
      const trigger = dropdown.querySelector('[role="combobox"]');
      trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const open = dropdown.classList.contains('is-open');
        this.closeDropdowns();
        dropdown.classList.toggle('is-open', !open);
        trigger.setAttribute('aria-expanded', String(!open));
      });

      dropdown.querySelectorAll('.cm-sh-swatch-option').forEach((option) => {
        option.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          dropdown.dataset.value = option.dataset.value;
          const swatch = dropdown.querySelector('[role="combobox"] .cm-sh-swatch');
          if (swatch && option.dataset.image) {
            swatch.style.backgroundImage = `url(${option.dataset.image})`;
          }
          const label = dropdown.querySelector('[role="combobox"] span:last-child');
          if (label) label.textContent = option.dataset.value;
          dropdown
            .querySelectorAll('.cm-sh-swatch-option')
            .forEach((other) => other.setAttribute('aria-selected', String(other === option)));
          this.closeDropdowns();
          this.syncUnit(unit);
        });
      });
    }

    this.syncUnit(unit);
  }

  closeDropdowns() {
    this.querySelectorAll('.cm-sh-swatch-dropdown.is-open').forEach((dropdown) => {
      dropdown.classList.remove('is-open');
      dropdown.querySelector('[role="combobox"]')?.setAttribute('aria-expanded', 'false');
    });
  }

  /** The option values currently chosen inside one unit, in option order. */
  unitOptions(unit) {
    return this.optionNames.map((name) => {
      const control = unit.querySelector(`[data-option-name="${CSS.escape(name)}"]`);
      if (!control) return null;
      return control.tagName === 'SELECT' ? control.value : control.dataset.value;
    });
  }

  matchVariant(options) {
    return this.variants.find((variant) =>
      options.every((value, index) => value == null || variant.options[index] === value)
    );
  }

  syncUnit(unit) {
    const variant = this.matchVariant(this.unitOptions(unit));
    unit.dataset.variantId = variant ? String(variant.id) : '';
    unit.dataset.available = variant && variant.available ? 'true' : 'false';

    const image = unit.querySelector('.cm-sh-unit__image');
    if (image && variant?.image) image.src = variant.image;
  }

  /* ---- add to cart ---- */

  get items() {
    if (!this.selectedDeal) return [];
    const items = Array.from(this.selectedDeal.querySelectorAll('.cm-sh-unit'))
      .map((unit) => Number(unit.dataset.variantId))
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));

    const gift = Number(this.selectedDeal.dataset.giftVariantId);
    if (gift) items.push({ id: gift, quantity: 1 });

    return items;
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.classList.add('is-visible');
  }

  clearError() {
    this.error?.classList.remove('is-visible');
  }

  async addToCart() {
    const items = this.items;
    const expected = Number(this.selectedDeal?.dataset.quantity || 0);
    const units = Array.from(this.selectedDeal?.querySelectorAll('.cm-sh-unit') || []).filter(
      (unit) => unit.dataset.variantId && unit.dataset.available === 'true'
    ).length;

    if (units !== expected || items.length === 0) {
      this.showError('One of the selections is unavailable. Please pick another size or colour.');
      return;
    }

    this.clearError();
    this.button.classList.add('is-busy');
    this.button.disabled = true;

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'Could not add to cart.');
      }

      await this.applyDiscount();
      window.location.href = checkoutUrl;
    } catch (error) {
      this.showError(error.message || 'Could not add to cart.');
      this.button.classList.remove('is-busy');
      this.button.disabled = false;
    }
  }

  /**
   * Bundle pricing is not enforced by the theme. Hitting /discount/CODE sets the
   * discount cookie so the reduced total shows in the cart and at checkout. The
   * code has to exist under Discounts for this to have any effect.
   */
  async applyDiscount() {
    const code = this.selectedDeal?.dataset.discountCode;
    if (!code) return;
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
    } catch (error) {
      console.warn('[cm-shorts] could not apply discount code', code, error);
    }
  }
}

if (!customElements.get('cm-shorts-bundle')) {
  customElements.define('cm-shorts-bundle', CmShortsBundle);
}

/* -------------------------------------------------------------------------- */
/* Gallery                                                                    */
/* -------------------------------------------------------------------------- */

class CmShortsGallery extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.cm-sh-lightbox');
    if (!this.dialog) return;

    this.querySelectorAll('[data-cm-sh-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.cmShZoom || 0);
        this.dialog.showModal();
        const target = this.dialog.querySelectorAll('img')[index];
        target?.scrollIntoView({ block: 'start' });
      });
    });

    this.dialog.querySelector('.cm-sh-lightbox__close')?.addEventListener('click', () => {
      this.dialog.close();
    });
  }
}

if (!customElements.get('cm-shorts-gallery')) {
  customElements.define('cm-shorts-gallery', CmShortsGallery);
}

/* -------------------------------------------------------------------------- */
/* Sticky buy box                                                             */
/* -------------------------------------------------------------------------- */

const STICKY_GAP = 20;

/** Height of the theme header while it is pinned to the top of the scroller. */
function pinnedHeaderHeight() {
  const header = document.querySelector('sticky-header, .section-header, .header');
  if (!header) return 0;
  const anchor = header.closest('.shopify-section') || header;
  if (getComputedStyle(anchor).position !== 'sticky') return 0;
  return header.getBoundingClientRect().height;
}

/**
 * A plain `top` pins the head of the buy box. Ours is taller than the viewport,
 * which left Add to cart permanently below the fold until the gallery ran out.
 * When it does not fit, pin its tail instead so the button stays visible.
 */
function syncBuyBoxOffset(info) {
  if (getComputedStyle(info).position !== 'sticky') {
    info.style.removeProperty('--sh-info-top');
    return;
  }
  const head = pinnedHeaderHeight();
  const overflow = info.offsetHeight - (window.innerHeight - head - STICKY_GAP * 2);
  const top = head + STICKY_GAP - Math.max(overflow, 0);
  info.style.setProperty('--sh-info-top', `${Math.round(top)}px`);
}

const buyBox = document.querySelector('.cm-sh-info');
if (buyBox) {
  const sync = () => syncBuyBoxOffset(buyBox);
  sync();
  window.addEventListener('resize', sync);
  /* The box changes height when a deal with more units is selected. */
  if ('ResizeObserver' in window) new ResizeObserver(sync).observe(buyBox);
}
